import { FC, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Divider,
    FormControlLabel,
    Switch,
    Chip,
} from '@mui/material';
import { Add, Delete, AutoAwesome } from '@mui/icons-material';
import {
    AiDifficulty,
    AiDifficultyLabels,
    WordbookTopicRequest,
    GenerateAiWordbookRequest,
    GenerateAiGroupLibraryRequest,
} from '../../models/library/aiLibraryRequest';
import { LibraryType } from '../../types/library';
import { useGenerateAiWordbook, useGenerateAiGroupLibrary } from '../../hooks/useLibrary';

interface AiLibraryGenerateDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

type GenerationType = 'single' | 'group';

const LANGUAGE_OPTIONS = [
    { value: 'en', label: '영어' },
    { value: 'ko', label: '한국어' },
    { value: 'ja', label: '일본어' },
    { value: 'zh', label: '중국어' },
    { value: 'es', label: '스페인어' },
    { value: 'fr', label: '프랑스어' },
    { value: 'de', label: '독일어' },
    { value: 'vi', label: '베트남어' },
];

const DEFAULT_WORDBOOK_TOPIC: WordbookTopicRequest = {
    title: '',
    topic: '',
    wordCount: 20,
    difficulty: AiDifficulty.EASY,
};

export const AiLibraryGenerateDialog: FC<AiLibraryGenerateDialogProps> = ({
    open,
    onClose,
    onSuccess,
}) => {
    const [generationType, setGenerationType] = useState<GenerationType>('single');
    const [headLang, setHeadLang] = useState('en');
    const [meaningLang, setMeaningLang] = useState('ko');
    const [registerToLibrary, setRegisterToLibrary] = useState(true);
    const [isVisible, setIsVisible] = useState(false);

    // 단일 단어장용
    const [singleTitle, setSingleTitle] = useState('');
    const [singleTopic, setSingleTopic] = useState('');
    const [singleWordCount, setSingleWordCount] = useState(20);
    const [singleDifficulty, setSingleDifficulty] = useState<AiDifficulty>(AiDifficulty.EASY);

    // 그룹용
    const [groupTitle, setGroupTitle] = useState('');
    const [wordbooks, setWordbooks] = useState<WordbookTopicRequest[]>([
        { ...DEFAULT_WORDBOOK_TOPIC },
    ]);

    const generateWordbook = useGenerateAiWordbook();
    const generateGroup = useGenerateAiGroupLibrary();

    const isLoading = generateWordbook.isPending || generateGroup.isPending;

    const handleAddWordbook = () => {
        setWordbooks([...wordbooks, { ...DEFAULT_WORDBOOK_TOPIC }]);
    };

    const handleRemoveWordbook = (index: number) => {
        if (wordbooks.length > 1) {
            setWordbooks(wordbooks.filter((_, i) => i !== index));
        }
    };

    const handleWordbookChange = (
        index: number,
        field: keyof WordbookTopicRequest,
        value: string | number | AiDifficulty
    ) => {
        const updated = [...wordbooks];
        updated[index] = { ...updated[index], [field]: value };
        setWordbooks(updated);
    };

    const handleSubmit = async () => {
        try {
            if (generationType === 'single') {
                const request: GenerateAiWordbookRequest = {
                    title: singleTitle,
                    topic: singleTopic,
                    headLang,
                    meaningLang,
                    wordCount: singleWordCount,
                    difficulty: singleDifficulty,
                    registerToLibrary,
                    libraryType: LibraryType.SYSTEM,
                    isVisible,
                };
                await generateWordbook.mutateAsync(request);
            } else {
                const request: GenerateAiGroupLibraryRequest = {
                    groupTitle,
                    headLang,
                    meaningLang,
                    wordbooks,
                    registerToLibrary,
                    libraryType: LibraryType.SYSTEM,
                    isVisible,
                };
                await generateGroup.mutateAsync(request);
            }
            onSuccess?.();
            handleClose();
        } catch (error) {
            console.error('AI generation failed:', error);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            // Reset form
            setGenerationType('single');
            setSingleTitle('');
            setSingleTopic('');
            setSingleWordCount(20);
            setSingleDifficulty(AiDifficulty.EASY);
            setGroupTitle('');
            setWordbooks([{ ...DEFAULT_WORDBOOK_TOPIC }]);
            setRegisterToLibrary(true);
            setIsVisible(false);
            onClose();
        }
    };

    const isValid = () => {
        if (generationType === 'single') {
            return singleTitle.trim() && singleTopic.trim() && singleWordCount > 0;
        } else {
            return (
                groupTitle.trim() &&
                wordbooks.length > 0 &&
                wordbooks.every((wb) => wb.title.trim() && wb.topic.trim() && wb.wordCount > 0)
            );
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <AutoAwesome color="primary" />
                    <Typography variant="h6">AI 단어장 생성</Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* 생성 유형 선택 */}
                <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom>
                        생성 유형
                    </Typography>
                    <ToggleButtonGroup
                        value={generationType}
                        exclusive
                        onChange={(_, value) => value && setGenerationType(value)}
                        fullWidth
                        disabled={isLoading}
                    >
                        <ToggleButton value="single">단일 단어장</ToggleButton>
                        <ToggleButton value="group">그룹 단어장</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* 공통: 언어 설정 */}
                <Box display="flex" gap={2} mb={3}>
                    <FormControl fullWidth disabled={isLoading}>
                        <InputLabel>단어 언어</InputLabel>
                        <Select
                            value={headLang}
                            onChange={(e) => setHeadLang(e.target.value)}
                            label="단어 언어"
                        >
                            {LANGUAGE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth disabled={isLoading}>
                        <InputLabel>의미 언어</InputLabel>
                        <Select
                            value={meaningLang}
                            onChange={(e) => setMeaningLang(e.target.value)}
                            label="의미 언어"
                        >
                            {LANGUAGE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* 단일 단어장 폼 */}
                {generationType === 'single' && (
                    <Box>
                        <TextField
                            fullWidth
                            label="단어장 제목"
                            value={singleTitle}
                            onChange={(e) => setSingleTitle(e.target.value)}
                            margin="normal"
                            disabled={isLoading}
                            placeholder="예: 길을 물어볼 때 사용하는 표현"
                        />
                        <TextField
                            fullWidth
                            label="주제 (AI가 참고할 내용)"
                            value={singleTopic}
                            onChange={(e) => setSingleTopic(e.target.value)}
                            margin="normal"
                            multiline
                            rows={2}
                            disabled={isLoading}
                            placeholder="예: 외국에서 길을 물어볼 때 사용하는 영어 표현들"
                            helperText="AI가 이 주제를 기반으로 단어를 생성합니다"
                        />
                        <Box display="flex" gap={2} mt={2}>
                            <TextField
                                type="number"
                                label="단어 개수"
                                value={singleWordCount}
                                onChange={(e) => setSingleWordCount(parseInt(e.target.value) || 20)}
                                disabled={isLoading}
                                inputProps={{ min: 5, max: 50 }}
                                sx={{ width: 150 }}
                            />
                            <FormControl sx={{ minWidth: 200 }} disabled={isLoading}>
                                <InputLabel>난이도</InputLabel>
                                <Select
                                    value={singleDifficulty}
                                    onChange={(e) => setSingleDifficulty(e.target.value as AiDifficulty)}
                                    label="난이도"
                                >
                                    {Object.entries(AiDifficultyLabels).map(([key, label]) => (
                                        <MenuItem key={key} value={key}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}

                {/* 그룹 단어장 폼 */}
                {generationType === 'group' && (
                    <Box>
                        <TextField
                            fullWidth
                            label="그룹 제목"
                            value={groupTitle}
                            onChange={(e) => setGroupTitle(e.target.value)}
                            margin="normal"
                            disabled={isLoading}
                            placeholder="예: 미국에서 많이 사용하는 표현"
                        />

                        <Box mt={2} mb={1} display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2">
                                단어장 목록 ({wordbooks.length}개)
                            </Typography>
                            <Button
                                startIcon={<Add />}
                                onClick={handleAddWordbook}
                                disabled={isLoading || wordbooks.length >= 10}
                                size="small"
                            >
                                단어장 추가
                            </Button>
                        </Box>

                        {wordbooks.map((wb, index) => (
                            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Chip label={`단어장 ${index + 1}`} size="small" />
                                        {wordbooks.length > 1 && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveWordbook(index)}
                                                disabled={isLoading}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <TextField
                                        fullWidth
                                        label="단어장 제목"
                                        value={wb.title}
                                        onChange={(e) => handleWordbookChange(index, 'title', e.target.value)}
                                        margin="dense"
                                        size="small"
                                        disabled={isLoading}
                                        placeholder="예: 길을 물어볼 때"
                                    />
                                    <TextField
                                        fullWidth
                                        label="주제"
                                        value={wb.topic}
                                        onChange={(e) => handleWordbookChange(index, 'topic', e.target.value)}
                                        margin="dense"
                                        size="small"
                                        disabled={isLoading}
                                        placeholder="예: 외국에서 길을 물어볼 때 사용하는 표현"
                                    />
                                    <Box display="flex" gap={2} mt={1}>
                                        <TextField
                                            type="number"
                                            label="단어 개수"
                                            value={wb.wordCount}
                                            onChange={(e) =>
                                                handleWordbookChange(index, 'wordCount', parseInt(e.target.value) || 20)
                                            }
                                            size="small"
                                            disabled={isLoading}
                                            inputProps={{ min: 5, max: 50 }}
                                            sx={{ width: 120 }}
                                        />
                                        <FormControl size="small" sx={{ minWidth: 150 }} disabled={isLoading}>
                                            <InputLabel>난이도</InputLabel>
                                            <Select
                                                value={wb.difficulty}
                                                onChange={(e) =>
                                                    handleWordbookChange(index, 'difficulty', e.target.value as AiDifficulty)
                                                }
                                                label="난이도"
                                            >
                                                {Object.entries(AiDifficultyLabels).map(([key, label]) => (
                                                    <MenuItem key={key} value={key}>
                                                        {label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* 라이브러리 등록 옵션 */}
                <Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={registerToLibrary}
                                onChange={(e) => setRegisterToLibrary(e.target.checked)}
                                disabled={isLoading}
                            />
                        }
                        label="라이브러리에 등록"
                    />

                    {registerToLibrary && (
                        <Box display="flex" gap={2} mt={1} alignItems="center">
                            <Alert severity="info" sx={{ flex: 1 }}>
                                AI 생성 후 라이브러리 목록에서 카테고리/큐레이션 섹션을 지정하세요.
                            </Alert>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isVisible}
                                        onChange={(e) => setIsVisible(e.target.checked)}
                                        disabled={isLoading}
                                    />
                                }
                                label="즉시 노출"
                            />
                        </Box>
                    )}
                </Box>

                {/* 에러 메시지 */}
                {(generateWordbook.isError || generateGroup.isError) && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {(generateWordbook.error as Error)?.message ||
                            (generateGroup.error as Error)?.message ||
                            'AI 생성 중 오류가 발생했습니다.'}
                    </Alert>
                )}

                {/* 진행 중 안내 */}
                {isLoading && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={20} />
                            <span>AI가 단어를 생성하고 있습니다. 잠시만 기다려주세요...</span>
                        </Box>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    취소
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!isValid() || isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : <AutoAwesome />}
                >
                    {isLoading ? '생성 중...' : 'AI 생성'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
